import { useAtom } from 'jotai';
import React, { FC, useEffect, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import { DefaultHeader } from '../../components/DefaultHeader';
import { LanguageName } from '../../components/LanguageName';
import { profileAtom } from '../../jotai-atoms/profile.atom';
import { Background } from '../../stories/Background';
import { Link } from '../../stories/Link';
import { getTimezoneOptionFromCtz } from '../../utils';
import {
  AccountInformationModal,
  ChangePasswordModal,
  EmergencyContactModal,
  ParentInformationModal,
  StudentInformationModal,
} from './components';
import { parseProfileResponse } from '../../utils/parsers/parseProfileResponse';
import { useGetProfile } from '../../hooks/use-get-profile';
import { ErrorPage } from '../Error/Error';
import { loadingGif } from '../../assets/images';
import styled from 'styled-components';

type ProfilePageProps = {
  csrftoken: string;
};

const AVAILABLE_MODALS = {
  StudentInformation: 'student-information',
  ChangePassword: 'change-password',
  AccountInformation: 'account-information',
  ParentInformation: 'parent-information',
  EmergencyContact: 'emergency-contact',
};

const LoadingContainer = styled.div`
  width: 100%;
  height: 130px;
  text-align: center;
  display: flex;
  justify-content: center;
  align-items: center;
`;

export const ProfilePage: FC<ProfilePageProps> = (props) => {
  const [activeModal, setActiveModal] = useState<string | null>(null);
  const [profile, setProfile] = useAtom(profileAtom);
  const __ = useTranslation().t;

  const { profileResponse, languages, timeZones, error, isLoading } = useGetProfile();
  useEffect(() => {
    if (profileResponse && languages && timeZones) {
      const profileData = parseProfileResponse(profileResponse.data)
      setProfile({
        _token: props.csrftoken,
        languageOptions: languages,
        timezoneOptions: timeZones,
        ...profileData,
      });
    }
  }, [profileResponse, languages, timeZones, props]);

  const timezoneOptionFromCtz = getTimezoneOptionFromCtz(profile?.accountInformation?.timezone);
  const currentTimezoneLabel =
    (profile?.timezoneOptions &&
      profile?.accountInformation?.timezone &&
      profile?.timezoneOptions[profile?.accountInformation?.timezone]) ||
    timezoneOptionFromCtz?.label;

  const PageHeader = () => (
    <div className="oejr-page-header">
      <Container>
        <h2>{__('myProfile')}</h2>
      </Container>
    </div>
  );

  const Section = (props: { children?: React.ReactNode }) => (
    <div className="oejr-profile-section">{props.children}</div>
  );

  if (isLoading) {
    return <LoadingContainer>
      <img src={loadingGif} alt="Loading" />
    </LoadingContainer>
  }

  if (error) {
    return <ErrorPage />
  }

  // NOTE: This might be used in the future
  // const UserProfilePicture = () => {
  //   return (
  //     <div className="d-flex justify-content-center">
  //       <img
  //         className="oejr-user-img"
  //         src={profile?.profilePicture || userPlaceholderSvg}
  //         alt="User"
  //       />
  //     </div>
  //   );
  // };

  return (
    <>
      <DefaultHeader />
      <Background>
        <div className="oejr-page-profile">
          <PageHeader />
          <Container>
            <div className="oejr-profile-row d-flex">
              <div className="oejr-profile-left-column">
                <Section>
                  {/* <UserProfilePicture /> */}
                  <h4>{__('studentInfo')}</h4>
                  <Row>
                    <Col>
                      <div className="label">{__('name')}</div>
                      <div className="value">{profile?.studentInformation?.firstName}</div>
                    </Col>
                    <Col>
                      <div className="label">{__('lastName')}</div>
                      <div className="value">{profile?.studentInformation?.lastName}</div>
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <div className="label">{__('email')}</div>
                      <div className="value">{profile?.studentInformation?.email}</div>
                    </Col>
                  </Row>
                  <hr />
                  <div className="text-center">
                    <Link onClick={() => setActiveModal(AVAILABLE_MODALS.StudentInformation)}>
                      {__('edit')}
                    </Link>
                  </div>
                </Section>
              </div>
              <div className="oejr-profile-right-column">
                <Section>
                  <h4>{__('accountInfo')}</h4>
                  <Row>
                    <Col>
                      <div className="label">{__('username')}</div>
                      <div className="value">{profile?.accountInformation?.username}</div>
                    </Col>
                    <Col>
                      <div className="label">{__('password')}</div>
                      <div className="value">
                        <span>********</span>
                        <Link
                          icon="pen"
                          onClick={() => setActiveModal(AVAILABLE_MODALS.ChangePassword)}
                        >
                          {__('change')}
                        </Link>
                      </div>
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <div className="label">{__('timezone')}</div>
                      <div className="value">{currentTimezoneLabel}</div>
                    </Col>
                    <Col>
                      <div className="label">{__('language')}</div>
                      <div className="value">
                        <LanguageName language={profile?.accountInformation?.language} />
                      </div>
                    </Col>
                  </Row>
                  <hr />
                  <div className="text-center">
                    <Link onClick={() => setActiveModal(AVAILABLE_MODALS.AccountInformation)}>
                      {__('edit')}
                    </Link>
                  </div>
                </Section>
                <Section>
                  <h4>{__('parentGuardianInfo')}</h4>
                  <Row>
                    <Col md={4} xs={6}>
                      <div className="label">{__('name')}</div>
                      <div className="value">{profile?.parentInformation?.firstName}</div>
                    </Col>
                    <Col md={4} xs={6}>
                      <div className="label">{__('lastName')}</div>
                      <div className="value">{profile?.parentInformation?.lastName}</div>
                    </Col>
                    <Col md={4} xs={12}>
                      <div className="label">{__('email')}</div>
                      <div className="value">{profile?.parentInformation?.email}</div>
                    </Col>
                  </Row>
                  <hr />
                  <div className="text-center">
                    <Link onClick={() => setActiveModal(AVAILABLE_MODALS.ParentInformation)}>
                      {__('edit')}
                    </Link>
                  </div>
                </Section>
                <Section>
                  <h4>{__('emergencyContactInfo')}</h4>
                  <Row>
                    <Col>
                      <div className="label">{__('fullName')}</div>
                      <div className="value">{profile?.emergencyInformation?.contact}</div>
                    </Col>
                    <Col>
                      <div className="label">{__('relationship')}</div>
                      <div className="value">
                        {profile?.emergencyInformation?.contactRelationship}
                      </div>
                    </Col>
                    <Col>
                      <div className="label">{__('phoneNumber')}</div>
                      <div className="value">{profile?.emergencyInformation?.contactPhone}</div>
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <div className="label">{__('country')}</div>
                      <div className="value">{profile?.emergencyInformation?.country}</div>
                    </Col>
                    <Col>
                      <div className="label">{__('region')}</div>
                      <div className="value">{profile?.emergencyInformation?.state}</div>
                    </Col>
                    <Col>
                      <div className="label">{__('address')}</div>
                      <div className="value">{profile?.emergencyInformation?.address}</div>
                    </Col>
                    <Col>
                      <div className="label">{__('city')}</div>
                      <div className="value">{profile?.emergencyInformation?.city}</div>
                    </Col>
                    <Col>
                      <div className="label">{__('zipCode')}</div>
                      <div className="value">{profile?.emergencyInformation?.zipCode}</div>
                    </Col>
                  </Row>
                  <hr />
                  <div className="text-center">
                    <Link onClick={() => setActiveModal(AVAILABLE_MODALS.EmergencyContact)}>
                      {__('edit')}
                    </Link>
                  </div>
                </Section>
              </div>
            </div>
          </Container>
        </div>
      </Background>
      <StudentInformationModal
        show={activeModal === AVAILABLE_MODALS.StudentInformation}
        onClose={() => setActiveModal(null)}
      />
      <ChangePasswordModal
        show={activeModal === AVAILABLE_MODALS.ChangePassword}
        onClose={() => setActiveModal(null)}
      />
      <AccountInformationModal
        show={activeModal === AVAILABLE_MODALS.AccountInformation}
        onClose={() => setActiveModal(null)}
      />
      <ParentInformationModal
        show={activeModal === AVAILABLE_MODALS.ParentInformation}
        onClose={() => setActiveModal(null)}
      />
      <EmergencyContactModal
        show={activeModal === AVAILABLE_MODALS.EmergencyContact}
        onClose={() => setActiveModal(null)}
      />
    </>
  );
};
